import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://ligentway.com";

const pageMetadata = {
  "/": {
    title: "Ligentway | AI Product Studio & Consultancy",
    description:
      "Ligentway builds useful AI products, apps, and productivity systems for ambitious teams.",
  },
  "/about": {
    title: "About Ligentway | AI Product Studio",
    description:
      "Meet Ligentway, an AI product studio and consultancy bringing strategy, design, and engineering together.",
  },
  "/services": {
    title: "AI Strategy, Products & Team Enablement | Ligentway",
    description:
      "Explore Ligentway services: AI opportunity mapping, product development, team enablement, and workflow redesign.",
  },
  "/case-studies": {
    title: "Case Studies | Ligentway",
    description:
      "See how Ligentway helps teams turn ambitious ideas into useful products and measurable progress.",
  },
  "/insights": {
    title: "Insights on AI, Products & Productivity | Ligentway",
    description:
      "Practical perspectives from Ligentway on AI adoption, product thinking, and better ways to work.",
  },
  "/faq": {
    title: "FAQ | Ligentway",
    description:
      "Answers to common questions about working with Ligentway on AI products, strategy, and productivity systems.",
  },
  "/contact": {
    title: "Contact Ligentway | Start a Conversation",
    description:
      "Have a product, AI, or productivity challenge? Start a conversation with Ligentway.",
  },
  "/philosophy": {
    title: "Our Approach | Ligentway",
    description:
      "Ligentway starts with the real work and focuses on useful, measurable AI solutions over hype.",
  },
  "/work": {
    title: "What We Do | Ligentway",
    description:
      "From AI products and strategy to productivity systems and embedded expertise, Ligentway helps teams move from ideas to impact.",
  },
};

function SEO() {
  const { pathname } = useLocation();
  const metadata = pageMetadata[pathname] || pageMetadata["/"];
  const canonicalPath = pageMetadata[pathname] ? pathname : "/";
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Ligentway OÜ",
        url: SITE_URL,
        logo: `${SITE_URL}/ligentway-mark.svg`,
        email: "contact@ligentway.com",
        description:
          "Ligentway is an AI product studio and consultancy building useful software, apps, and productivity systems.",
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "Ligentway",
        description: metadata.description,
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
    ],
  };

  return (
    <Helmet>
      <html lang="en" />
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:site_name" content="Ligentway" />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${SITE_URL}/ligentway-cover.png`} />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:description" content={metadata.description} />
      <meta name="twitter:image" content={`${SITE_URL}/ligentway-cover.png`} />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}

export default SEO;
